import { useEffect } from "react";

import Cookies from "js-cookie";

import { isClient } from "./dom.ts";
import { getLocalStorage, setLocalStorage } from "./localstorage.ts";

export type PageType = "SEO" | "PFM";
export type TeamType =
    | ""
    | "Marketing"
    | "Operations"
    | "HR"
    | "Design"
    | "Program Management"
    | "Software Engineering"
    | "Customer Service"
    | "Legal"
    | "Finance"
    | "Sales"
    | "Software Development" // deprecated
    | "IT Support"; // deprecated

export type JourneyLocalStoragePersona = {
    team?: TeamType;
    teamTypeInferredMl?: TeamType;
    persona?: string;
    keyword?: string;
    subAnchor?: string;
};
export type JourneyLocalStorage = JourneyLocalStoragePersona & {
    anchor?: {
        name?: string;
        type?: PageType;
    };
};
export type BXPAnchorCookie = {
    anchor: string;
    type: PageType;
};
const BXP_ANCHOR_COOKIE_NAME = "bxp_gateway_anchor";
const GWP_LOCAL_STORAGE_NAME = "onboarding-journey";
const COOKIE_EXPIRY = 60 * 60 * 24 * 7; // 7 days

/**
 * Custom hook to create the local storage using bxp anchor cookie and also update the local storage with the persona and team,
 * the name of the local storage will be "onboarding-journey"
 * @param {JourneyLocalStoragePersona} value - persona object to update in the local storage.
 *      - persona (deprecated): May be used to reflect content changes.
 *      - team: May be set early on in customer flow, this should be used for user-defined teamType values.
 *              WARNING: Setting this value results in skipping the Team Selection Survey (Signup Confirmation Page -> UserSeg Survey).
 *              Keep this value an empty string or undefined, if you do not want customers to skip the Team Selection Survey.
 *      - teamTypeInferredMl: teamType value that is passed in from downstream. This value is ML-inferred.
 *      - keyword: keyword param (the same keyword passed in from downstream).
 *      - subAnchor: subAnchor param sent from bxp-gateway
 *              WARNING:  Setting this value may result in bypassing the Content Type selection step in provisioning screen(Signup Confirmation page -> Provisioning step -> ContentType is skipped if subAnchor matches an entry in the subAnchor map).
 * @returns function to get the localstorage.
 * @returns function to update the localstorage, which accepts a value parameter.
 * @example Here are some example of the usage
 *
 *     ```
 *      useAnchorLocalStorage();
 *         // OR
 *      useAnchorLocalStorage({ persona: "Marketing", team: "Marketing" });
 *          // OR
 *       const handleLocalStorage = useAnchorLocalStorage();
 *       handleLocalStorage({ persona: "Marketing", team: "Marketing" });
 *      ```
 */
export function useAnchorLocalStorage(value?: JourneyLocalStoragePersona) {
    useEffect(() => {
        updateLocalStorage(value);
    }, [value]);

    return {
        getAnchorLocalStorage: () => {
            return isClient && getLocalStorage(GWP_LOCAL_STORAGE_NAME);
        },
        setAnchorLocalStorage: updateLocalStorage
    };
    /* Create or update localstorage.
     * create : Using bxp_anchor_cookie, if present, if not present, create with ampty anchor object {anchor: {}}
     * update : Using existing localstorage, or existing cookie and persona value passed.
     */
    function updateLocalStorage(value?: JourneyLocalStoragePersona) {
        if (isClient) {
            const bxpGatewayAnchorCookie = Cookies.get(BXP_ANCHOR_COOKIE_NAME);
            const journeyLocalStorage = getLocalStorage(GWP_LOCAL_STORAGE_NAME);
            try {
                const bxpGatewayAnchorObj = JSON.parse(
                    bxpGatewayAnchorCookie || "null"
                ) as BXPAnchorCookie;
                const currentLocalStorageObj = JSON.parse(
                    journeyLocalStorage || "null"
                ) as JourneyLocalStorage;
                const anchorStorage: JourneyLocalStorage =
                    currentLocalStorageObj
                        ? { ...currentLocalStorageObj, ...value }
                        : bxpGatewayAnchorObj
                        ? {
                              anchor: {
                                  name: bxpGatewayAnchorObj?.anchor,
                                  type: bxpGatewayAnchorObj?.type
                              },
                              ...value
                          }
                        : {};
                setLocalStorage(
                    GWP_LOCAL_STORAGE_NAME,
                    JSON.stringify(anchorStorage),
                    COOKIE_EXPIRY
                );
            } catch (err) {
                console.error("Error creating local storage", err);
            }
        }
    }
}
